import { Link } from 'react-router-dom';
import { UtensilsCrossed, Home, Search, Clock } from 'lucide-react';
import { useStore } from '@/store';
import { useStoreInfo } from '@/lib/store';

export function Footer() {
  const currentYear = new Date().getFullYear();
  const darkMode = useStore((state) => state.darkMode);
  const { storeInfo } = useStoreInfo();

  const mobileNavItems = [
    { name: 'Home', icon: Home, href: '/' },
    { name: 'Browse', icon: Search, href: '/products' },
    { name: 'Orders', icon: Clock, href: '/track' },
  ];

  return (
    <footer className={darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}>
      {/* Desktop Footer */}
      <div className="hidden md:block border-t dark:border-gray-700">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-6">
                {storeInfo?.site_logo ? (
                  <img 
                    src={storeInfo.site_logo.startsWith('http') ? storeInfo.site_logo : `https://marivieint.com/${storeInfo.site_logo}`}
                    alt={storeInfo.site_name} 
                    className="h-8 w-auto"
                  />
                ) : (
                  <>
                    <UtensilsCrossed className="h-6 w-6 text-brand-primary" />
                    <span className="text-lg font-display font-bold text-brand-primary">
                      {storeInfo?.site_name || 'Plymouth Braider'}
                    </span>
                  </>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {storeInfo?.slogan || 'Plymouth Braider'}
              </p>
            </div>

            <div>
              <h3 className="font-display font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/products" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    Menu
                  </Link>
                </li>
                <li>
                  <Link to="/categories" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    Categories
                  </Link>
                </li>
                <li>
                  <Link to="/track" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    Track Order
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    FAQ
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-display font-semibold mb-4">About Us</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    Our Story
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    Contact
                  </Link>
                </li>
                <li>
                  <Link to="/privacy-policy" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/return-policy" className="text-sm text-gray-500 hover:text-brand-primary dark:text-gray-400 dark:hover:text-brand-light">
                    Return Policy
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-display font-semibold mb-4">Contact Us</h3>
              {storeInfo?.phone_num && (
                <div className="space-y-2 mb-4">
                  {storeInfo.phone_num.split(',').map((number, index) => (
                    <p key={index} className="text-sm text-gray-500 dark:text-gray-400">
                      <a href={`tel:${number.trim()}`} className="hover:text-brand-primary dark:hover:text-brand-light">
                        {number.trim()}
                      </a>
                    </p>
                  ))}
                </div>
              )}
              {storeInfo?.contact_email && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Email: {storeInfo.contact_email}
                </p>
              )}
              {storeInfo?.shop_address && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Address: {storeInfo.shop_address}
                </p>
              )}
            </div>
          </div>

          <div className="border-t dark:border-gray-700 mt-12 pt-8 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              © {currentYear} {storeInfo?.site_name || 'Plymouth Braider'}. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden bg-white dark:bg-gray-800 border-t dark:border-gray-700 z-50">
        <div className="grid grid-cols-3">
          {mobileNavItems.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className="flex flex-col items-center justify-center py-3"
            >
              <item.icon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
              <span className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                {item.name}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </footer>
  );
}